/**
 * QBR Schedule Section - Displays Quarterly Business Review schedule
 * linked to contract governance.
 *
 * CR-QBR: Replaces the disconnected Audit Schedule section with
 * contract-linked QBR/review scheduling.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Loader2, Calendar, FileText, ExternalLink, AlertCircle,
  Clock, CheckCircle2, AlertTriangle, User,
} from 'lucide-react';
import { suppliersAPI, QBRScheduleItem, QBRScheduleResponse } from '../../lib/api';
import { format, isValid } from 'date-fns';
import ContractPriceDetailModal from '../../components/purchasing/ContractPriceDetailModal';

interface QBRScheduleSectionProps {
  supplierId: number;
}

/**
 * Safely format a date string with fallback.
 */
function formatDate(dateString: string | null | undefined, formatStr: string, fallback = 'Not recorded'): string {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (!isValid(date)) return fallback;
  return format(date, formatStr);
}

/**
 * Get status badge configuration.
 */
function getStatusConfig(status: string): { color: string; bgColor: string; icon: React.ReactNode; label: string } {
  const configs: Record<string, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
    pending: {
      color: 'text-gray-600',
      bgColor: 'bg-gray-100 dark:bg-gray-800',
      icon: <Clock className="h-3.5 w-3.5" />,
      label: 'Pending',
    },
    scheduled: {
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900',
      icon: <Calendar className="h-3.5 w-3.5" />,
      label: 'Scheduled',
    },
    completed: {
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900',
      icon: <CheckCircle2 className="h-3.5 w-3.5" />,
      label: 'Completed',
    },
    overdue: {
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900',
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
      label: 'Overdue',
    },
  };
  return configs[status] || configs['pending'];
}

export function QBRScheduleSection({ supplierId }: QBRScheduleSectionProps) {
  const [data, setData] = useState<QBRScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await suppliersAPI.getQBRSchedule(supplierId);
      setData(response.data);
    } catch (err: unknown) {
      console.error('Failed to load QBR schedule:', err);
      setError('Failed to load QBR schedule');
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleContractClick = (contractId: number) => {
    setSelectedContractId(contractId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          QBR & Review Schedule
        </CardTitle>
        <CardDescription>
          Quarterly Business Review and contract review schedule linked to contract governance
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
            <p className="font-medium text-red-600">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={fetchSchedule}>
              Try Again
            </Button>
          </div>
        ) : !data?.has_schedule ? (
          <EmptyState />
        ) : (
          <div className="space-y-4">
            {data.schedule_items.map((item) => (
              <QBRScheduleCard
                key={item.contract_id}
                item={item}
                onClick={() => handleContractClick(item.contract_id)}
              />
            ))}
          </div>
        )}

        {/* Contract Detail Modal */}
        {selectedContractId && (
          <ContractPriceDetailModal
            contractId={selectedContractId}
            isOpen={true}
            onClose={() => setSelectedContractId(null)}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Empty state when no QBR schedule is configured.
 */
function EmptyState() {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
      <p className="font-medium">No QBR schedule configured</p>
      <p className="text-sm mt-1 max-w-md mx-auto">
        Configure Business Review frequency on supplier contracts to enable QBR tracking.
        Set the &quot;BR Frequency (months)&quot; field on active contracts.
      </p>
    </div>
  );
}

/**
 * Individual QBR schedule card.
 * Shows all QBR info inline - clicking opens contract detail modal if user has access.
 */
function QBRScheduleCard({ item, onClick }: { item: QBRScheduleItem; onClick: () => void }) {
  const statusConfig = getStatusConfig(item.status);

  return (
    <div
      className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{item.contract_number}</span>
          {item.customer_name && (
            <span className="text-sm text-muted-foreground">
              ({item.customer_name})
            </span>
          )}
        </div>
        <Badge className={`${statusConfig.bgColor} ${statusConfig.color} gap-1`}>
          {statusConfig.icon}
          {statusConfig.label}
        </Badge>
      </div>

      {/* Details grid */}
      <div className="grid gap-4 md:grid-cols-4 text-sm">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
            Review Frequency
          </label>
          <p className="font-medium">{item.review_frequency_display}</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
            Last Review
          </label>
          <p className="font-medium">
            {formatDate(item.last_review_date, 'MMM d, yyyy')}
          </p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
            Next Review
          </label>
          <p className={`font-medium ${item.status === 'overdue' ? 'text-red-600' : ''}`}>
            {formatDate(item.next_review_date, 'MMM d, yyyy', 'Not scheduled')}
            {item.days_until_review !== null && item.days_until_review >= 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({item.days_until_review} days)
              </span>
            )}
            {item.days_until_review !== null && item.days_until_review < 0 && (
              <span className="text-xs text-red-500 ml-1">
                ({Math.abs(item.days_until_review)} days overdue)
              </span>
            )}
          </p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
            Owner
          </label>
          <p className="font-medium flex items-center gap-1">
            {item.owner_name ? (
              <>
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                {item.owner_name}
              </>
            ) : (
              <span className="text-muted-foreground">Not assigned</span>
            )}
          </p>
        </div>
      </div>

      {/* Footer link hint */}
      <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
        <ExternalLink className="h-3 w-3" />
        Click to view contract details
      </div>
    </div>
  );
}
